import React, { useEffect, useState } from 'react';
import clsx from 'clsx';
import {
  Box,
  Card,
  CardContent,
  CardMedia,
  colors,
  IconButton,
  Typography,
} from '@mui/material';
import LockIcon from '@mui/icons-material/Lock';
import {
  ContextMenuState,
  isDir,
  isFile,
  Resource,
  useResourceModal,
} from '../../../../modules/resources';
import {
  useFilesActions,
  useFileThumbnail,
} from '../../../../modules/files/hooks';
import { FileIcon, LazyImage } from '../../../../components';
import { useUserIsSuperAdmin } from '../../../../modules/auth/hooks';
import { ResourceModal } from '../../../../modules/ui';
import ResourceIcon from './ResourceIcon';

const classes = {
  root: {
    position: 'relative',
    cursor: 'pointer',
    color: colors.blueGrey[500],
  },
  media: {
    display: 'flex',
    flexDirection: 'row',
    flexWrap: 'nowrap',
    justifyContent: 'center',
    alignItems: 'center',
    height: 132,
    '& > img': {
      width: '100%',
      height: '100%',
      objectFit: 'cover',
      userSelect: 'none',
    },
  },
  placeholder: {
    height: 132,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: 'text.secondary',
  },
  icon: {
    height: '64px',
    width: '64px',
    fontSize: '64px',
  },
  content: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
  },
  label: {
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    userSelect: 'none',
    pb: 2,
    px: 1,
  },
  selected: {
    backgroundColor: colors.blueGrey[50],
  },
  drawerSelected: {
    color: colors.blueGrey[700],
    backgroundColor: colors.blueGrey[50],
    '& $label': {
      fontWeight: 'bold',
    },
  },
  lockButton: {
    position: 'absolute',
    top: '8px',
    right: '8px',
    padding: 0,
  },
  lockIcon: {
    fontSize: '14px',
  },
};

interface CardProps {
  resource: Resource;
  isSelected?: boolean;
  onDoubleClick(event: React.MouseEvent): void;
  onClick(): void;
  onContextMenu(data: ContextMenuState): void;
}

const GridItem = (props: CardProps | any) => {
  const {
    resource,
    onClick,
    onDoubleClick,
    onContextMenu,
    className,
    selectedResource,
    isSelected = false,
    ...rest
  } = props;
  const { getThumb } = useFilesActions();
  const thumbnail = useFileThumbnail(resource.id);
  const { openModal } = useResourceModal();
  const isSuperAdmin = useUserIsSuperAdmin();

  const [hasThumb, setHasThumb] = useState<boolean>(
    isFile(resource) && !!thumbnail,
  );

  useEffect(() => {
    if (isFile(resource)) {
      getThumb(resource);
    }
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    setHasThumb(isFile(resource) && !!thumbnail);
  }, [thumbnail, resource]);

  const onContextMenuClick = (event: MouseEvent) => {
    event.preventDefault();
    onContextMenu({
      resource: resource,
      x: event.clientX,
      y: event.clientY,
    });
  };

  const handleLockIconClick = () => {
    if (isDir(resource)) {
      openModal(ResourceModal.Permissions, resource);
    }
  };

  const isCurrentSelected = () =>
    selectedResource &&
    selectedResource.type === resource.type &&
    selectedResource.id === resource.id;

  return (
    <Card
      {...rest}
      onClick={onClick}
      onDoubleClick={onDoubleClick}
      onContextMenu={onContextMenuClick}
      sx={{
        ...(isSelected && classes.selected),
        ...(isCurrentSelected() && classes.drawerSelected),
        position: 'relative',
      }}
      className={clsx(classes.root, className)}
    >
      {isSuperAdmin && isDir(resource) && false === resource.isPublic && (
        <IconButton
          disableRipple
          sx={classes.lockButton}
          onClick={handleLockIconClick}
          size="large"
        >
          <LockIcon sx={classes.lockIcon} />
        </IconButton>
      )}
      {hasThumb ? (
        <CardMedia sx={classes.media}>
          <LazyImage
            src={thumbnail}
            placeholder={({ ref }) => (
              <Box ref={ref} sx={classes.placeholder}>
                <FileIcon style={classes.icon} type={resource.fileType} />
              </Box>
            )}
            error={() => (
              <Box sx={classes.placeholder}>
                <FileIcon style={classes.icon} type={resource.fileType} />
              </Box>
            )}
          />
        </CardMedia>
      ) : (
        <Box sx={classes.placeholder}>
          {isDir(resource) && (
            <ResourceIcon style={classes.icon} item={resource} />
          )}
          {isFile(resource) && (
            <FileIcon style={classes.icon} type={resource.fileType} />
          )}
        </Box>
      )}
      <CardContent sx={classes.content}>
        <Typography variant="h6" sx={classes.label}>
          {isDir(resource) && resource.title}
          {isFile(resource) && resource.filename}
        </Typography>
      </CardContent>
    </Card>
  );
};

export default GridItem;
